// Per-output schedules — list, add, edit, delete.
//
// Backend encoding (mirrors the Flutter app):
//   Weekly           = comma flags "1,0,1,0,1,0,0" (Sun..Sat)
//   FunctionOnTimer  = minutes from midnight to switch ON  (0 = not set)
//   FunctionOffTimer = minutes from midnight to switch OFF (0 = not set)
//   OutputValue      = 1/0
// A schedule can carry an ON time, an OFF time, or both.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box, Typography, Card, IconButton, Stack, Button, TextField, Chip,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, Dialog, Alert,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DeviceShell from '../components/DeviceShell';
import {
  getSchedules, addSchedule, updateSchedule, deleteSchedule,
  getDeviceDetail, getOutputs,
} from '../../../core/api/api';
import { rowForOutput } from '../../../core/devices/buttonStatus';
import { FIT_XS } from '../../shared/fitDialog';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toMinutes = (t) => {
  const [hh, mm] = (t || '00:00').split(':').map(Number);
  return (hh || 0) * 60 + (mm || 0);
};
const toTime = (m) => {
  const n = Number(m) || 0;
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
};
const parseWeekly = (w) => (w ? w.split(',').map((f, i) => (f === '1' ? i : -1)).filter((i) => i >= 0) : []);
const buildWeekly = (idxs) => DAYS.map((_, i) => (idxs.includes(i) ? '1' : '0')).join(',');
const formatDays = (idxs) => {
  if (idxs.length === 0) return 'No days';
  if (idxs.length === 7) return 'Every day';
  return idxs.map((i) => DAYS[i]).join(', ');
};

const EMPTY = { id: null, name: '', onEnabled: true, onTime: '08:00', offEnabled: false, offTime: '18:00', days: [0, 1, 2, 3, 4, 5, 6] };

export default function SchedulesScreen() {
  const { uniqueNumber, output } = useParams();
  const { state } = useLocation();
  const out = Number(output);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // ── Where am I? ──────────────────────────────────────────────────────────
  // Location › Room › Device › Output, FETCHED rather than taken from the
  // router state. The state is lost on a reload or a deep link, and the two
  // callers disagree about its shape anyway — the desktop passes `name`, the
  // mobile passes `outputName`, and the header read only `name`, so on a phone
  // it always fell through to "Output N". Fetching makes the header correct
  // from every entry point, including none.
  const [where, setWhere] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      getDeviceDetail(uniqueNumber).then((r) => (Array.isArray(r.data) ? r.data[0] : null)).catch(() => null),
      getOutputs(uniqueNumber).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
    ]).then(([dev, outs]) => {
      if (!alive) return;
      const row = rowForOutput(outs, out);
      setWhere({
        location: (dev && dev.LocationName) || '',
        room: (dev && dev.RoomName) || '',
        device: (dev && dev.DeviceName) || '',
        // Falls back through the caller's state before giving up and naming the
        // number, so the line is never empty.
        output: (row && row.OutputName) || state?.outputName || state?.name || `Output ${out}`,
      });
    });
    return () => { alive = false; };
  }, [uniqueNumber, out, state]);

  // One line, in the order the building is: site, then room, then the panel on
  // the wall, then the button on it.
  const crumbs = [where?.location, where?.room, where?.device, where?.output]
    .map((s) => String(s || '').trim())
    .filter(Boolean);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSchedules(uniqueNumber, out);
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [uniqueNumber, out]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditorOpen(true); };
  const openEdit = (s) => {
    const on = Number(s.FunctionOnTimer) || 0;
    const off = Number(s.FunctionOffTimer) || 0;
    setForm({
      id: s.SchedulerID,
      name: s.SchedulerName || '',
      onEnabled: on > 0,
      onTime: toTime(on || 8 * 60),
      offEnabled: off > 0,
      offTime: toTime(off || 18 * 60),
      days: parseWeekly(s.Weekly),
    });
    setEditorOpen(true);
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const valid = (form.onEnabled || form.offEnabled) && form.days.length > 0;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    const data = {
      SchedulerName: form.name.trim() || 'Schedule',
      Weekly: buildWeekly(form.days),
      FunctionOnTimer: form.onEnabled ? toMinutes(form.onTime) : 0,
      FunctionOffTimer: form.offEnabled ? toMinutes(form.offTime) : 0,
      OutputValue: form.onEnabled ? 1 : 0,
    };
    try {
      if (form.id != null) {
        await updateSchedule(data, `SchedulerID = ${form.id}`);
      } else {
        await addSchedule({ ...data, UniqueNumber: uniqueNumber, Output: out });
      }
      setEditorOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await deleteSchedule(`SchedulerID = ${id}`);
    await load();
  };

  return (
    <DeviceShell
      title={(
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {where?.output || `Output ${out}`}
          </Typography>
          {/* The path to it. Wraps rather than truncating on a phone: which
              room a schedule belongs to is the thing you came here to check. */}
          <Typography
            variant="caption"
            sx={{ display: 'block', opacity: 0.85, lineHeight: 1.3 }}
          >
            {crumbs.length > 1 ? crumbs.slice(0, -1).join(' › ') : 'Schedules'}
          </Typography>
        </Box>
      )}
      actions={(
        <Tooltip title="Add schedule">
          <IconButton color="inherit" onClick={openNew}><AddIcon /></IconButton>
        </Tooltip>
      )}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={1.5}>
          {/* ⚠️ Output 0 is not a relay on any device — outputs are 1-based.
              A schedule saved against it stores fine, lists fine, and can never
              fire, which is the worst possible combination. It only became
              reachable through an off-by-one in the mobile TouchScreen (now
              fixed); this says so for any row that already exists, since the
              only way to delete one is from this screen. */}
          {out < 1 && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={700}>
                {`Output ${out} is not a real output.`}
              </Typography>
              <Typography variant="caption">
                Outputs are numbered from 1, so nothing here can ever run. These rows
                were created by a bug that has been fixed — delete them and set the
                schedule again on the button itself.
              </Typography>
            </Alert>
          )}

          {list.length === 0 && (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
              <ScheduleIcon sx={{ fontSize: 44, opacity: 0.5 }} />
              <Typography sx={{ mt: 1 }}>No schedules yet.</Typography>
            </Box>
          )}

          {list.map((s) => {
            const on = Number(s.FunctionOnTimer) || 0;
            const off = Number(s.FunctionOffTimer) || 0;
            const idxs = parseWeekly(s.Weekly);
            return (
              <Card key={s.SchedulerID} sx={{ p: 2, borderRadius: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} color="text.primary" noWrap>{s.SchedulerName || 'Schedule'}</Typography>
                    <Stack direction="row" spacing={1} sx={{ my: 0.75 }} useFlexGap flexWrap="wrap">
                      {on > 0 && <Chip size="small" color="success" icon={<PowerSettingsNewIcon />} label={`ON ${toTime(on)}`} />}
                      {off > 0 && <Chip size="small" variant="outlined" icon={<PowerSettingsNewIcon />} label={`OFF ${toTime(off)}`} />}
                      {on === 0 && off === 0 && <Chip size="small" label="No time set" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{formatDays(idxs)}</Typography>
                  </Box>
                  <IconButton onClick={() => openEdit(s)}><EditIcon /></IconButton>
                  <IconButton onClick={() => remove(s.SchedulerID)}><DeleteIcon /></IconButton>
                </Box>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} {...FIT_XS}>
        <DialogTitle>{form.id != null ? 'Edit Schedule' : 'New Schedule'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Name" value={form.name} onChange={(e) => patch({ name: e.target.value })} fullWidth />

            <Box>
              <FormControlLabel
                control={<Checkbox checked={form.onEnabled} onChange={(e) => patch({ onEnabled: e.target.checked })} />}
                label="Turn ON at"
              />
              <TextField
                type="time" value={form.onTime} onChange={(e) => patch({ onTime: e.target.value })}
                disabled={!form.onEnabled} size="small" InputLabelProps={{ shrink: true }} sx={{ ml: 1 }}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={form.offEnabled} onChange={(e) => patch({ offEnabled: e.target.checked })} />}
                label="Turn OFF at"
              />
              <TextField
                type="time" value={form.offTime} onChange={(e) => patch({ offTime: e.target.value })}
                disabled={!form.offEnabled} size="small" InputLabelProps={{ shrink: true }} sx={{ ml: 1 }}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Repeat on</Typography>
                <Button size="small" onClick={() => patch({ days: form.days.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6] })}>
                  {form.days.length === 7 ? 'Clear' : 'Every day'}
                </Button>
              </Box>
              <ToggleButtonGroup
                value={form.days}
                onChange={(_, v) => patch({ days: v })}
                size="small" sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {DAYS.map((d, i) => (
                  <ToggleButton key={d} value={i} sx={{ borderRadius: 2, px: 1.25 }}>{d}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {!valid && (
              <Typography variant="caption" color="error">
                Pick at least one day and enable an ON or OFF time.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!valid || saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DeviceShell>
  );
}
